import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Save, Package, Calendar, Wrench, Settings as SettingsIcon, Inbox, Receipt } from "lucide-react";

/* ===================== AUTH ===================== */
export function AdminLogin() {
    const nav = useNavigate();
    const [email, setEmail] = useState("admin@tennis.com");
    const [password, setPassword] = useState("admin123");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data?.token) localStorage.setItem("admin_token", data.token);
            toast.success("Welcome back.");
            nav("/admin");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0c] text-white flex items-center justify-center p-6" data-testid="admin-login-page">
            <form onSubmit={submit} className="w-full max-w-md border border-white/10 bg-[#111114] p-8">
                <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-2">Admin</div>
                <h1 className="text-3xl font-semibold mb-8">Sign in</h1>
                <label className="block mb-4">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50">Email</span>
                    <input
                        type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                        data-testid="admin-login-email"
                        className="mt-2 w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:border-[#a6ce39] outline-none"
                    />
                </label>
                <label className="block mb-6">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50">Password</span>
                    <input
                        type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                        data-testid="admin-login-password"
                        className="mt-2 w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:border-[#a6ce39] outline-none"
                    />
                </label>
                <button type="submit" disabled={busy} data-testid="admin-login-submit"
                    className="w-full bg-[#a6ce39] text-black font-semibold py-3 disabled:opacity-60">
                    {busy ? "Signing in…" : "Sign in"}
                </button>
                <Link to="/" className="block mt-6 text-center text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white">← Back to site</Link>
            </form>
        </div>
    );
}

/* ===================== DASHBOARD SHELL ===================== */
const TABS = [
    { id: "products", label: "Store", icon: Package },
    { id: "events", label: "Events", icon: Calendar },
    { id: "services", label: "Services", icon: Wrench },
    { id: "settings", label: "Settings", icon: SettingsIcon },
    { id: "contacts", label: "Contacts", icon: Inbox },
    { id: "transactions", label: "Transactions", icon: Receipt },
];

export default function AdminDashboard() {
    const nav = useNavigate();
    const [tab, setTab] = useState("products");
    const [me, setMe] = useState(null);

    useEffect(() => {
        api.get("/auth/me").then((r) => setMe(r.data)).catch(() => nav("/admin/login"));
    }, [nav]);

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem("admin_token");
        nav("/admin/login");
    };

    if (!me) {
        return <div className="min-h-screen bg-[#0a0a0c] text-white/60 flex items-center justify-center">Loading…</div>;
    }

    return (
        <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="admin-dashboard">
            <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-white/40">Tennis Ops · Admin</div>
                    <div className="text-lg font-semibold">{me.email}</div>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/" className="text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white">View site →</Link>
                    <button onClick={logout} data-testid="admin-logout"
                        className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-white/20 px-3 py-2 hover:bg-white/5">
                        <LogOut size={14} /> Logout
                    </button>
                </div>
            </header>

            <div className="flex flex-wrap gap-1 border-b border-white/10 px-6">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                            className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${
                                tab === t.id ? "border-[#a6ce39] text-white" : "border-transparent text-white/50 hover:text-white"
                            }`}>
                            <Icon size={14} /> {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="p-6">
                {tab === "products" && <ProductsPanel />}
                {tab === "events" && <EventsPanel />}
                {tab === "services" && <ServicesPanel />}
                {tab === "settings" && <SettingsPanel />}
                {tab === "contacts" && <ContactsPanel />}
                {tab === "transactions" && <TransactionsPanel />}
            </div>
        </div>
    );
}

/* ===================== SHARED HELPERS ===================== */
function TextField({ label, value, onChange, testid, type = "text" }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</span>
            <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
                data-testid={testid}
                className="mt-1 w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-[#a6ce39] outline-none" />
        </label>
    );
}

function SelectField({ label, value, onChange, options, testid }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</span>
            <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="mt-1 w-full bg-[#111114] border border-white/15 px-3 py-2 text-sm focus:border-[#a6ce39] outline-none">
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </label>
    );
}

function ToggleField({ label, value, onChange, testid }) {
    return (
        <label className="flex items-center gap-3 mt-2">
            <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
                data-testid={testid}
                className="w-4 h-4 accent-[#a6ce39]" />
            <span className="text-xs text-white/70">{label}</span>
        </label>
    );
}

/* ===================== PRODUCTS ===================== */
const PRODUCT_CATS = [
    { value: "racquet", label: "Racquet" },
    { value: "balls", label: "Balls" },
    { value: "string", label: "String" },
    { value: "overgrip", label: "Installed Overgrip" },
    { value: "overgrip_pack_3", label: "Overgrip Three-Pack" },
    { value: "dampener", label: "Dampener" },
    { value: "replacement_grip", label: "Replacement Grip" },
    { value: "apparel", label: "Apparel" },
    { value: "accessories", label: "Other Accessory" },
];

function blankProduct() {
    return {
        id: "",
        category: "racquet",
        name: "",
        brand_name: "",
        price: 0,
        sale_price: "",
        sale_active: false,
        condition: "New",
        image: "",
        description: "",
        color: "",
        color_hex: "#ffffff",
        string_type: "",
        shape: "",
        gauge: "",
        grip_type: "",
        thickness: "",
        available_for_install: true,
        eligible_for_expedited: false,
        enabled: true,
        stock: 1,
    };
}

function ProductsPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/admin/products").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            await api.post("/admin/products", editing);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        await api.delete(`/admin/products/${id}`);
        load();
    };

    return (
        <div data-testid="admin-products-panel">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold">Store products</h2>
                <button onClick={() => setEditing(blankProduct())} data-testid="admin-add-product"
                    className="flex items-center gap-2 bg-[#a6ce39] text-black px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold">
                    <Plus size={14} /> Add product
                </button>
            </div>
            <div className="grid gap-3">
                {items.map((p) => (
                    <div key={p.id} className="border border-white/10 bg-[#111114] p-4 flex items-center gap-4" data-testid={`admin-product-row-${p.id}`}>
                        {p.image ? (
                            <img src={p.image} alt="" className="w-16 h-16 object-cover bg-black/40" onError={(e) => (e.currentTarget.style.opacity = 0.3)} />
                        ) : (
                            <div className="w-16 h-16 bg-black/40 border border-white/10" />
                        )}
                        <div className="flex-1">
                            <div className="text-sm font-semibold">{p.name}</div>
                            <div className="text-xs text-white/50">{p.category} · ${p.price} · {p.enabled ? "Live" : "Hidden"} · stock {p.stock ?? "—"}</div>
                        </div>
                        <button onClick={() => setEditing(p)} className="text-xs uppercase tracking-[0.2em] px-3 py-2 border border-white/20 hover:bg-white/5" data-testid={`admin-edit-product-${p.id}`}>Edit</button>
                        <button onClick={() => remove(p.id)} className="text-xs px-2 py-2 border border-red-400/30 text-red-300 hover:bg-red-500/10" data-testid={`admin-del-product-${p.id}`}><Trash2 size={14} /></button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
                    <div className="bg-[#0f0f12] border border-white/15 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-product-editor">
                        <h3 className="text-xl font-semibold mb-4">{editing.id ? "Edit product" : "New product"}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <TextField label="Brand" value={editing.brand_name} onChange={(v) => setEditing({ ...editing, brand_name: v })} testid="admin-product-brand" />
                            <TextField label="Product name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="admin-product-name" />
                            <SelectField label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={PRODUCT_CATS} testid="admin-product-category" />
                            <TextField label="Add-on / retail price (USD)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testid="admin-product-price" />
                            <TextField label="Sale price (optional)" type="number" value={editing.sale_price ?? ""} onChange={(v) => setEditing({ ...editing, sale_price: v === "" ? null : v })} testid="admin-product-sale-price" />
                            <TextField label="Stock" type="number" value={editing.stock} onChange={(v) => setEditing({ ...editing, stock: v })} testid="admin-product-stock" />
                            <TextField label="Color name" value={editing.color} onChange={(v) => setEditing({ ...editing, color: v })} testid="admin-product-color" />
                            <TextField label="Color swatch (hex)" value={editing.color_hex} onChange={(v) => setEditing({ ...editing, color_hex: v })} testid="admin-product-color-hex" />
                            <TextField label="Image URL" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} testid="admin-product-image" />
                            <TextField label="Condition" value={editing.condition} onChange={(v) => setEditing({ ...editing, condition: v })} testid="admin-product-condition" />

                            {editing.category === "string" && (
                                <>
                                    <SelectField
                                        label="String category"
                                        value={editing.string_type}
                                        onChange={(v) => setEditing({ ...editing, string_type: v })}
                                        options={[
                                            { value: "synthetic", label: "Synthetic Gut" },
                                            { value: "multi", label: "Multifilament" },
                                            { value: "poly", label: "Polyester" },
                                        ]}
                                        testid="admin-product-string-type"
                                    />
                                    <SelectField
                                        label="Shape"
                                        value={editing.shape}
                                        onChange={(v) => setEditing({ ...editing, shape: v })}
                                        options={[
                                            { value: "", label: "Not specified" },
                                            { value: "round", label: "Round" },
                                            { value: "shaped", label: "Shaped" },
                                        ]}
                                        testid="admin-product-shape"
                                    />
                                    <SelectField
                                        label="Gauge"
                                        value={editing.gauge}
                                        onChange={(v) => setEditing({ ...editing, gauge: v })}
                                        options={[
                                            { value: "", label: "Not specified" },
                                            { value: "16", label: "16" },
                                            { value: "16L", label: "16L" },
                                            { value: "17", label: "17" },
                                            { value: "18", label: "18" },
                                        ]}
                                        testid="admin-product-gauge"
                                    />
                                </>
                            )}

                            {editing.category === "overgrip" && (
                                <>
                                    <SelectField
                                        label="Overgrip type"
                                        value={editing.grip_type}
                                        onChange={(v) => setEditing({ ...editing, grip_type: v })}
                                        options={[
                                            { value: "tacky", label: "Tacky" },
                                            { value: "dry", label: "Dry" },
                                        ]}
                                        testid="admin-product-grip-type"
                                    />
                                    <TextField label="Thickness (optional)" value={editing.thickness} onChange={(v) => setEditing({ ...editing, thickness: v })} testid="admin-product-thickness" />
                                </>
                            )}
                        </div>
                        <label className="block mt-3">
                            <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">Description</span>
                            <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                data-testid="admin-product-description"
                                className="mt-1 w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-[#a6ce39] outline-none" />
                        </label>
                        <div className="mt-4 grid gap-2 sm:grid-cols-2">
                            <ToggleField label="Live on site" value={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} testid="admin-product-enabled" />
                            <ToggleField label="Sale active" value={editing.sale_active} onChange={(v) => setEditing({ ...editing, sale_active: v })} testid="admin-product-sale-active" />
                            <ToggleField label="Available for Lab installation" value={editing.available_for_install} onChange={(v) => setEditing({ ...editing, available_for_install: v })} testid="admin-product-install" />
                            <ToggleField label="Eligible for expedited service" value={editing.eligible_for_expedited} onChange={(v) => setEditing({ ...editing, eligible_for_expedited: v })} testid="admin-product-expedited" />
                        </div>
                        <div className="flex gap-3 mt-6 justify-end">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em]">Cancel</button>
                            <button onClick={save} data-testid="admin-product-save"
                                className="flex items-center gap-2 bg-[#a6ce39] text-black font-semibold px-5 py-2 text-xs uppercase tracking-[0.2em]">
                                <Save size={14} /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ===================== EVENTS ===================== */
function blankEvent() {
    return { id: "", title: "", date: "", time: "", location: "", coach: "", price: 0, spots_left: 10, level: "All Levels", enabled: true };
}

function EventsPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/admin/events").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            await api.post("/admin/events", editing);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this event?")) return;
        await api.delete(`/admin/events/${id}`);
        load();
    };

    return (
        <div data-testid="admin-events-panel">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold">Events & clinics</h2>
                <button onClick={() => setEditing(blankEvent())} data-testid="admin-add-event"
                    className="flex items-center gap-2 bg-[#a6ce39] text-black px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold">
                    <Plus size={14} /> Add event
                </button>
            </div>
            <div className="grid gap-3">
                {items.map((e) => (
                    <div key={e.id} className="border border-white/10 bg-[#111114] p-4 flex items-center gap-4" data-testid={`admin-event-row-${e.id}`}>
                        <div className="w-20 text-center border-r border-white/10 pr-4">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{new Date(e.date).toLocaleDateString("en-US", { month: "short" })}</div>
                            <div className="text-3xl font-semibold text-[#a6ce39]">{new Date(e.date).getDate() || "?"}</div>
                        </div>
                        <div className="flex-1">
                            <div className="text-sm font-semibold">{e.title}</div>
                            <div className="text-xs text-white/50">{e.time} · {e.location}</div>
                            <div className="text-xs text-white/40">${e.price} · {e.spots_left} spots · {e.enabled ? "Live" : "Hidden"}</div>
                        </div>
                        <button onClick={() => setEditing(e)} className="text-xs uppercase tracking-[0.2em] px-3 py-2 border border-white/20" data-testid={`admin-edit-event-${e.id}`}>Edit</button>
                        <button onClick={() => remove(e.id)} className="text-xs px-2 py-2 border border-red-400/30 text-red-300" data-testid={`admin-del-event-${e.id}`}><Trash2 size={14} /></button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
                    <div className="bg-[#0f0f12] border border-white/15 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-event-editor">
                        <h3 className="text-xl font-semibold mb-4">{editing.id ? "Edit event" : "New event"}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <TextField label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="admin-event-title" />
                            <TextField label="Date (YYYY-MM-DD)" value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} testid="admin-event-date" />
                            <TextField label="Time" value={editing.time} onChange={(v) => setEditing({ ...editing, time: v })} testid="admin-event-time" />
                            <TextField label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testid="admin-event-location" />
                            <TextField label="Coach" value={editing.coach} onChange={(v) => setEditing({ ...editing, coach: v })} testid="admin-event-coach" />
                            <TextField label="Level" value={editing.level} onChange={(v) => setEditing({ ...editing, level: v })} testid="admin-event-level" />
                            <TextField label="Price (USD)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testid="admin-event-price" />
                            <TextField label="Spots left" type="number" value={editing.spots_left} onChange={(v) => setEditing({ ...editing, spots_left: v })} testid="admin-event-spots" />
                        </div>
                        <ToggleField label="Live on site" value={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} testid="admin-event-enabled" />
                        <div className="flex gap-3 mt-6 justify-end">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em]">Cancel</button>
                            <button onClick={save} data-testid="admin-event-save"
                                className="flex items-center gap-2 bg-[#a6ce39] text-black font-semibold px-5 py-2 text-xs uppercase tracking-[0.2em]">
                                <Save size={14} /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ===================== SERVICES ===================== */
const SERVICE_CATS = [
    { value: "coaching", label: "Coaching" },
    { value: "lab", label: "The Lab" },
    { value: "human-performance", label: "Human Performance" },
    { value: "competition", label: "Competition" },
    { value: "education", label: "Education" },
];
const PAYMENT_MODES = [
    { value: "online", label: "Pay online (Stripe)" },
    { value: "request", label: "Request-only (contact form)" },
];

function blankService() {
    return { id: "", category: "coaching", name: "", tagline: "", description: "", price_from: 0, unit: "hr", payment_mode: "request", enabled: true };
}

function ServicesPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/admin/services").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try { await api.post("/admin/services", editing); toast.success("Saved"); setEditing(null); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this service?")) return;
        await api.delete(`/admin/services/${id}`); load();
    };

    return (
        <div data-testid="admin-services-panel">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold">Services</h2>
                <button onClick={() => setEditing(blankService())} data-testid="admin-add-service"
                    className="flex items-center gap-2 bg-[#a6ce39] text-black px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold">
                    <Plus size={14} /> Add service
                </button>
            </div>
            <div className="grid gap-3">
                {items.map((s) => (
                    <div key={s.id} className="border border-white/10 bg-[#111114] p-4 flex items-center gap-4" data-testid={`admin-service-row-${s.id}`}>
                        <div className="flex-1">
                            <div className="text-sm font-semibold">{s.name}</div>
                            <div className="text-xs text-white/50">{s.category} · from ${s.price_from} / {s.unit}</div>
                            <div className="text-xs text-white/40">
                                <span className={s.payment_mode === "online" ? "text-[#a6ce39]" : "text-white/40"}>
                                    {s.payment_mode === "online" ? "Pays online" : "Request-only"}
                                </span> · {s.enabled ? "Live" : "Hidden"}
                            </div>
                        </div>
                        <button onClick={() => setEditing(s)} className="text-xs uppercase tracking-[0.2em] px-3 py-2 border border-white/20" data-testid={`admin-edit-service-${s.id}`}>Edit</button>
                        <button onClick={() => remove(s.id)} className="text-xs px-2 py-2 border border-red-400/30 text-red-300" data-testid={`admin-del-service-${s.id}`}><Trash2 size={14} /></button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
                    <div className="bg-[#0f0f12] border border-white/15 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-service-editor">
                        <h3 className="text-xl font-semibold mb-4">{editing.id ? "Edit service" : "New service"}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <TextField label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="admin-service-name" />
                            <SelectField label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={SERVICE_CATS} testid="admin-service-category" />
                            <TextField label="Tagline" value={editing.tagline} onChange={(v) => setEditing({ ...editing, tagline: v })} testid="admin-service-tagline" />
                            <TextField label="Unit (hr, session, racquet…)" value={editing.unit} onChange={(v) => setEditing({ ...editing, unit: v })} testid="admin-service-unit" />
                            <TextField label="Price from (USD)" type="number" value={editing.price_from} onChange={(v) => setEditing({ ...editing, price_from: v })} testid="admin-service-price" />
                            <SelectField label="Payment mode" value={editing.payment_mode} onChange={(v) => setEditing({ ...editing, payment_mode: v })} options={PAYMENT_MODES} testid="admin-service-mode" />
                        </div>
                        <label className="block mt-3">
                            <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">Description</span>
                            <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                data-testid="admin-service-description"
                                className="mt-1 w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-[#a6ce39] outline-none" />
                        </label>
                        <ToggleField label="Live on site" value={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} testid="admin-service-enabled" />
                        <div className="flex gap-3 mt-6 justify-end">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em]">Cancel</button>
                            <button onClick={save} data-testid="admin-service-save"
                                className="flex items-center gap-2 bg-[#a6ce39] text-black font-semibold px-5 py-2 text-xs uppercase tracking-[0.2em]">
                                <Save size={14} /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ===================== SETTINGS ===================== */
function SettingsPanel() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
    const save = async () => {
        try {
            const { data } = await api.post("/admin/settings", {
                swing_free_limit: Number(s.swing_free_limit),
                swing_ai_price: Number(s.swing_ai_price),
                swing_human_price: Number(s.swing_human_price),
            });
            setS(data);
            toast.success("Settings saved");
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    };
    if (!s) return <div className="text-white/50">Loading…</div>;
    return (
        <div className="max-w-2xl" data-testid="admin-settings-panel">
            <h2 className="text-2xl font-semibold mb-6">Swing AI paywall</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <TextField label="Free analyses per email" type="number" value={s.swing_free_limit} onChange={(v) => setS({ ...s, swing_free_limit: v })} testid="admin-swing-free-limit" />
                <TextField label="AI price after limit (USD)" type="number" value={s.swing_ai_price} onChange={(v) => setS({ ...s, swing_ai_price: v })} testid="admin-swing-ai-price" />
                <TextField label="Human coach review (USD)" type="number" value={s.swing_human_price} onChange={(v) => setS({ ...s, swing_human_price: v })} testid="admin-swing-human-price" />
            </div>
            <button onClick={save} data-testid="admin-settings-save"
                className="mt-6 flex items-center gap-2 bg-[#a6ce39] text-black font-semibold px-5 py-3 text-xs uppercase tracking-[0.2em]">
                <Save size={14} /> Save settings
            </button>
        </div>
    );
}

/* ===================== CONTACTS ===================== */
function ContactsPanel() {
    const [contacts, setContacts] = useState([]);
    const [stringing, setStringing] = useState([]);
    useEffect(() => {
        api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
        api.get("/admin/stringing-requests").then((r) => setStringing(r.data)).catch(() => {});
    }, []);
    return (
        <div data-testid="admin-contacts-panel" className="space-y-10">
            <div>
                <h2 className="text-2xl font-semibold mb-4">Contact submissions ({contacts.length})</h2>
                <div className="grid gap-2">
                    {contacts.map((c) => (
                        <div key={c.id} className="border border-white/10 bg-[#111114] p-4 text-sm" data-testid={`admin-contact-${c.id}`}>
                            <div className="flex justify-between text-xs text-white/50">
                                <span>{c.brand} · {c.subject}</span><span>{new Date(c.created_at).toLocaleString()}</span>
                            </div>
                            <div className="mt-1 font-semibold">{c.name} · {c.email} {c.phone ? `· ${c.phone}` : ""}</div>
                            <div className="mt-1 text-white/70 whitespace-pre-wrap">{c.message}</div>
                        </div>
                    ))}
                    {contacts.length === 0 && <div className="text-white/40 text-sm">No submissions yet.</div>}
                </div>
            </div>
            <div>
                <h2 className="text-2xl font-semibold mb-4">Stringing requests ({stringing.length})</h2>
                <div className="grid gap-2">
                    {stringing.map((s) => (
                        <div key={s.id} className="border border-white/10 bg-[#111114] p-4 text-sm" data-testid={`admin-string-${s.id}`}>
                            <div className="flex justify-between text-xs text-white/50">
                                <span>{s.brand} · {s.turnaround}</span><span>{new Date(s.created_at).toLocaleString()}</span>
                            </div>
                            <div className="mt-1 font-semibold">{s.name} · {s.email}</div>
                            <div className="mt-1 text-white/70">{s.racquet_brand} {s.racquet_model} · {s.string_choice} @ {s.tension_main}{s.tension_cross ? `/${s.tension_cross}` : ""} lb</div>
                            {s.customization_notes && <div className="mt-1 text-white/50 text-xs">{s.customization_notes}</div>}
                        </div>
                    ))}
                    {stringing.length === 0 && <div className="text-white/40 text-sm">No stringing requests yet.</div>}
                </div>
            </div>
        </div>
    );
}

/* ===================== TRANSACTIONS ===================== */
function TransactionsPanel() {
    const [txs, setTxs] = useState([]);
    useEffect(() => { api.get("/admin/transactions").then((r) => setTxs(r.data)); }, []);
    return (
        <div data-testid="admin-transactions-panel">
            <h2 className="text-2xl font-semibold mb-4">Payment transactions ({txs.length})</h2>
            <div className="overflow-x-auto border border-white/10">
                <table className="w-full text-sm">
                    <thead className="bg-[#111114] text-white/60 text-xs uppercase tracking-[0.15em]">
                        <tr>
                            <th className="text-left px-4 py-3">When</th>
                            <th className="text-left px-4 py-3">Type</th>
                            <th className="text-left px-4 py-3">Item</th>
                            <th className="text-left px-4 py-3">Customer</th>
                            <th className="text-right px-4 py-3">Amount</th>
                            <th className="text-left px-4 py-3">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {txs.map((t) => (
                            <tr key={t.session_id} className="border-t border-white/10" data-testid={`admin-tx-${t.session_id}`}>
                                <td className="px-4 py-3 text-white/60 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                                <td className="px-4 py-3">{t.item_type}</td>
                                <td className="px-4 py-3 text-white/70">{t.description}</td>
                                <td className="px-4 py-3 text-white/70">{t.customer_email || "—"}</td>
                                <td className="px-4 py-3 text-right">${t.amount?.toFixed(2)}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs px-2 py-1 border ${t.payment_status === "paid" ? "border-[#a6ce39] text-[#a6ce39]" : "border-white/20 text-white/60"}`}>
                                        {t.payment_status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                        {txs.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No transactions yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
